import { useEffect, useRef, useState } from "react";
import { Page } from "@iq/models";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import useUserStore from "../states/user";
import {
	createQuestions,
	deleteQuestion,
	fetchQuestions,
	updateQuestion,
} from "../services/questionnaireService";
import "../styles/questionnaire.css";

function Questionaire() {
	const organizationId = useUserStore((state) => state.session?.organization_id);
	const [questions, setQuestions] = useState([]);
	const [draft, setDraft] = useState("");
	const [editingId, setEditingId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [refresh, setRefresh] = useState(0);
	const busyRef = useRef(false);
	const inputRef = useRef(null);

	useEffect(() => {
		if (!organizationId) return undefined;
		let active = true;
		setLoading(true);
		setLoadError("");
		fetchQuestions()
			.then((items) => {
				if (active) setQuestions(items);
			})
			.catch((err) => {
				if (active) setLoadError(err.message || "Failed to load questions.");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => { active = false; };
	}, [organizationId, refresh]);

	const unavailable = !organizationId || loading || saving || !!loadError;

	async function mutate(action, successMessage) {
		if (busyRef.current || unavailable) return;
		busyRef.current = true;
		setSaving(true);
		setError("");
		setMessage("");
		try {
			await action();
			setDraft("");
			setEditingId(null);
			setMessage(successMessage);
			setLoading(true);
			setRefresh((value) => value + 1);
		} catch (err) {
			setError(err.message || "Failed to save changes.");
		} finally {
			busyRef.current = false;
			setSaving(false);
		}
	}

	function handleSubmit(event) {
		event.preventDefault();
		const question = draft.trim();
		if (!question || Array.from(question).length > 255) {
			setError("Enter a question between 1 and 255 characters.");
			return;
		}
		mutate(
			() => editingId === null ? createQuestions([question]) : updateQuestion(editingId, question),
			editingId === null ? "Question added." : "Question updated.",
		);
	}

	function startEditing(question) {
		setEditingId(question.id);
		setDraft(question.question);
		setError("");
		setMessage("");
		inputRef.current?.focus();
	}

	function handleDelete(question) {
		if (busyRef.current || unavailable) return;
		if (!window.confirm(`Delete "${question.question}"? This cannot be undone.`)) return;
		mutate(() => deleteQuestion(question.id), "Question deleted.");
	}

	return (
		<Page>
			<div className="questionnairePage">
				<header>
					<h2>Checkout questions</h2>
					<p>Manage the questions customers answer when booking a service.</p>
				</header>
				{message && <p role="status">{message}</p>}
				{error && <p className="questionnaireError" role="alert">{error}</p>}
				{loadError && (
					<div className="questionnaireError" role="alert">
						<p>{loadError} Use Retry to refresh the list before making more changes.</p>
						<button className="btn" onClick={() => { setLoading(true); setRefresh((value) => value + 1); }}>Retry</button>
					</div>
				)}
				<form className="questionnaireEditor" onSubmit={handleSubmit}>
					<label htmlFor="questionnaire-question">{editingId === null ? "New question" : "Edit question"}</label>
					<textarea
						ref={inputRef}
						id="questionnaire-question"
						rows={3}
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						disabled={unavailable}
						placeholder="For example: Do you have any allergies?"
					/>
					<div className="questionnaireActions">
						<button className="btn questionnairePrimary" type="submit" disabled={unavailable}>
							{editingId === null && <FiPlus aria-hidden="true" />}
							{saving ? "Saving..." : editingId === null ? "Add question" : "Save changes"}
						</button>
						{editingId !== null && (
							<button className="btn" type="button" disabled={saving} onClick={() => {
								setEditingId(null);
								setDraft("");
								setError("");
							}}>Cancel</button>
						)}
					</div>
				</form>
				<section aria-label="Saved questions" aria-busy={loading}>
					<h3>Saved questions</h3>
					{(!organizationId || loading) && <p role="status">Loading questions...</p>}
					{!loading && !loadError && questions.length === 0 && <p>No questions yet. Add your first checkout question above.</p>}
					<ol className="questionnaireList">
						{questions.map((question) => (
							<li className="questionnaireRow" key={question.id}>
								<p>{question.question}</p>
								<div className="questionnaireActions">
									<button className="btn" disabled={unavailable || editingId !== null} onClick={() => startEditing(question)} aria-label={`Edit question: ${question.question}`}>
										<FiEdit2 aria-hidden="true" /> Edit
									</button>
									<button className="btn questionnaireDelete" disabled={unavailable || editingId !== null} onClick={() => handleDelete(question)} aria-label={`Delete question: ${question.question}`}>
										<FiTrash2 aria-hidden="true" /> Delete
									</button>
								</div>
							</li>
						))}
					</ol>
				</section>
			</div>
		</Page>
	);
}

export default Questionaire;